import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { Quote } from '@invest-copilot/shared';
import { fetchTwseRealtimeFromBrowser, type TwseRealtimeQuote } from './twse-realtime';
import { isTwOpenNow } from './market';

/**
 * 對台股 symbol 自動加掛 TWSE 即時源（可選）
 *
 * - 非台股：直接回傳 yahoo quote
 * - 後端 /api/quote 已優先走 TWSE 即時源，前端預設不再直連 mis.twse.com.tw，避免正式站 CORS 噴錯
 * - 若本機要實驗瀏覽器直連，可設 VITE_ENABLE_BROWSER_TWSE=1
 * - TWSE 間歇失敗（撮合空檔 z='-' 或網路錯誤）：ref 保留上次成功資料，避免中英名閃爍
 */
export function useEnhancedQuote(symbol: string, yahooQuote?: Quote): Quote | undefined {
  const isTW = /\.(TW|TWO)$/i.test(symbol);
  const enableBrowserTwse = import.meta.env.VITE_ENABLE_BROWSER_TWSE === '1';
  // 只在明確 opt-in 時 polling TWSE；正式站使用後端 /api/quote 的 TWSE 優先邏輯。
  const isMarketActive = enableBrowserTwse && isTW && isTwOpenNow();

  // 保留「最後一次成功的 TWSE data」，避免間歇失敗造成回到 yahoo 英文名
  const lastTwseRef = useRef<TwseRealtimeQuote | null>(null);
  useEffect(() => { lastTwseRef.current = null; }, [symbol]);

  const twse = useQuery({
    queryKey: ['twse-realtime', symbol],
    queryFn: ({ signal }) => fetchTwseRealtimeFromBrowser(symbol, signal),
    enabled: isTW && !!yahooQuote && isMarketActive,
    refetchInterval: 3_000,
    staleTime: 2_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });

  if (twse.data) lastTwseRef.current = twse.data;
  const effective = twse.data ?? lastTwseRef.current;

  if (!yahooQuote) return undefined;
  if (!isTW) return yahooQuote;
  // 沒有 browser 直連 TWSE 資料時，尊重後端 /api/quote 已決策的 dataSource
  // （後端 getTwRealtimeQuote 已走「NAS bridge 富邦/mis.twse → 直連 mis.twse → yahoo」，
  //  台股盤中通常回 dataSource:'twse'＝即時）。不可無條件覆寫成 'yahoo'，否則 K 線 overlay
  //  與「TWSE 即時」標記會失效、現價與 K 線停在 yahoo 較舊值（2026-06-12 修）。
  if (!effective) return { ...yahooQuote, dataSource: yahooQuote.dataSource ?? 'yahoo' };

  return {
    ...yahooQuote,
    price: effective.price,
    prevClose: effective.prevClose,
    change: effective.change,
    changePct: effective.changePct,
    dayHigh: effective.high ?? yahooQuote.dayHigh,
    dayLow: effective.low ?? yahooQuote.dayLow,
    volume: effective.volume ?? yahooQuote.volume,
    shortName: effective.name ?? yahooQuote.shortName,
    fetchedAt: new Date().toISOString(),
    dataSource: 'twse',
    sourceTs: effective.ts,
  };
}
